import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Upload, ExternalLink, ShieldCheck, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { inr, readB64, METHOD_LABEL, errMsg } from "./util";

const n = (v) => (v || v === 0 ? v : null);
const lc = (v) => (Number(v) >= 50 ? "Unlimited" : v || 0);

export function profileSections(p) {
  const pr = p?.profile || {};
  const f = pr.fees || {}, w = pr.fee_waiver || {}, r = pr.rewards || {}, rd = pr.redemption || {}, ex = pr.exclusions || {}, lg = pr.lounge || {}, ch = pr.charges || {}, el = pr.eligibility || {};
  return [
    ["fees", "Joining & annual fee", `${inr(f.joining_inr)} joining · ${inr(f.annual_inr)} / yr`, f.text],
    ["rewards", "Rewards & cashback", `${n(r.base_value_pct) ?? "—"}% base value · ${r.type || ""}`, [r.earn_text, r.caps_text].filter(Boolean).join(" ")],
    ["redemption", "Redemption rate", rd.point_value_inr ? `₹${rd.point_value_inr} per point` : rd.best_option || "—", rd.text],
    ["exclusions", "Category exclusions", (ex.categories || []).join(", ") || "None stated", ex.text],
    ["lounge", "Lounge benefits", `${lc(lg.domestic_per_year)} domestic · ${lc(lg.international_per_year)} international / yr`, lg.text],
    ["forex_markup_pct", "Forex markup", pr.forex_markup_pct || pr.forex_markup_pct === 0 ? `${pr.forex_markup_pct}%` : "—", null],
    ["fee_waiver", "Fee waiver", w.annual_spend_inr ? `Spend ${inr(w.annual_spend_inr)} / yr` : "—", w.text],
    ["charges", "Interest & penalties", ch.interest_monthly_pct ? `${ch.interest_monthly_pct}% / month` : "—", [ch.late_fee_text, ch.cash_advance_text, ch.other_text].filter(Boolean).join(" ")],
    ["milestones", "Milestone benefits", (pr.milestones || []).length ? `${pr.milestones.length} milestone${pr.milestones.length > 1 ? "s" : ""}` : "None", (pr.milestones || []).map((m) => `${inr(m.annual_spend_inr)}: ${m.benefit}`).join(" · ")],
    ["eligibility", "Eligibility", [el.min_age && `Age ${el.min_age}${el.max_age ? `–${el.max_age}` : "+"}`, el.min_income_annual_inr && `${inr(el.min_income_annual_inr)}/yr income`].filter(Boolean).join(" · ") || "—", el.text],
  ];
}

export function SourceChip({ p }) {
  if (!p) return null;
  if (p.status === "processing") return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 text-[10px] font-bold px-2 py-0.5" data-testid="card-profile-status"><Loader2 className="w-3 h-3 animate-spin" /> Reading issuer site…</span>;
  if (p.status === "failed") return <span className="rounded-full bg-red-50 text-red-700 text-[10px] font-bold px-2 py-0.5" data-testid="card-profile-status">Profile failed</span>;
  if (p.method === "ai_quick") return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 text-[10px] font-bold px-2 py-0.5" data-testid="card-profile-status"><Sparkles className="w-3 h-3" /> Quick AI summary · official data loading</span>;
  const ai = p.method === "ai_research";
  return <span className={`inline-flex items-center gap-1 rounded-full text-[10px] font-bold px-2 py-0.5 ${ai ? "bg-stone-100 text-stone-600" : "bg-[#E8F3EE] text-[#0B4D3A]"}`} data-testid="card-profile-status">{ai ? <Sparkles className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}{METHOD_LABEL[p.method] || "Profile ready"}{p.refreshing && <Loader2 className="w-3 h-3 animate-spin ml-0.5" />}</span>;
}

export function useProfile({ id, issuer, cardName, enabled = true }) {
  const [p, setP] = useState(null);
  const t = useRef();
  useEffect(() => {
    if (!enabled || (!id && !(issuer && cardName))) return undefined;
    let alive = true;
    let n = 0;
    const load = async () => {
      n += 1;
      try {
        const { data } = id ? await api.get(`/card-lens/profiles/${id}`) : await api.get("/card-lens/profiles/lookup", { params: { issuer, card_name: cardName } });
        if (!alive) return;
        setP(data);
        if ((data?.status === "processing" || data?.method === "ai_quick") && n < 18) t.current = setTimeout(load, 10000);
      } catch { if (alive) setP({ status: "failed" }); }
    };
    load();
    return () => { alive = false; clearTimeout(t.current); };
  }, [id, issuer, cardName, enabled]);
  return [p, setP];
}

export default function CardProfileDialog({ open, onClose, id, issuer, cardName }) {
  const [p, setP] = useProfile({ id, issuer, cardName, enabled: open });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const refresh = async () => { setBusy(true); try { const { data } = await api.post(`/card-lens/profiles/${p.id}/refresh`); setP(data); toast.success("Re-reading the issuer site"); } catch (e) { toast.error(errMsg(e, "Refresh failed")); } setBusy(false); };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const { data } = await api.post(`/card-lens/profiles/${p.id}/document`, { filename: file.name, data_b64: await readB64(file) }); setP(data); toast.success("Document added — profile is being rebuilt"); }
    catch (e) { toast.error(errMsg(e, "Upload failed")); }
    setBusy(false);
  };
  const pr = p?.profile || {};
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="card-profile-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-black">{pr.card_name || cardName || p?.card_name || "Card profile"}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2 -mt-2">
          <span className="text-sm text-stone-500">{pr.issuer || issuer || p?.issuer}{pr.network ? ` · ${pr.network}` : ""}{pr.tier ? ` · ${pr.tier}` : ""}</span>
          <SourceChip p={p} />
          {p?.id && <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full" onClick={refresh} disabled={busy || p.status === "processing"} data-testid="card-profile-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
            <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="card-profile-doc-input" />
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="card-profile-upload-doc"><Upload className="w-3.5 h-3.5 mr-1" /> Official benefit PDF</Button>
          </div>}
        </div>
        {!p || p.status === "processing" ? (
          <div className="py-12 text-center text-sm text-stone-500" data-testid="card-profile-loading"><Loader2 className="w-6 h-6 mx-auto animate-spin text-[#0B4D3A] mb-2" />Reading the issuer's official card page and MITC / KFS documents… (about a minute)</div>
        ) : p.status === "failed" ? (
          <div className="py-8 text-center text-sm text-red-700" data-testid="card-profile-error">Couldn't build this profile. Try Refresh, or upload the issuer's benefit PDF.</div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-2.5 mt-2">
              {profileSections(p).map(([k, label, val, text]) => (
                <div key={k} className="rounded-xl border border-stone-200 p-3" data-testid={`card-profile-${k}`}>
                  <div className="text-[10px] font-black uppercase tracking-wider text-stone-400">{label}</div>
                  <div className="font-bold text-stone-900 text-sm mt-0.5">{val}</div>
                  {text && <div className="text-xs text-stone-600 mt-1 leading-relaxed">{text}</div>}
                  {p.field_sources?.[k] === -1 && <div className="text-[10px] text-stone-400 mt-1">General knowledge — not found on the official page</div>}
                </div>
              ))}
            </div>
            {(pr.perks?.other || []).length > 0 && <div className="mt-3 text-xs text-stone-600" data-testid="card-profile-perks"><b>Other perks:</b> {pr.perks.other.join(" · ")}</div>}
            <div className="mt-4 border-t border-stone-200 pt-3" data-testid="card-profile-sources">
              <div className="text-[10px] font-black uppercase tracking-wider text-stone-400 mb-1.5">Sources · auto-approved {p.updated_at ? new Date(p.updated_at).toLocaleDateString("en-IN") : ""}</div>
              {(p.sources || []).length === 0 && <div className="text-xs text-stone-500">No official page could be read — profile is AI research. Upload the issuer's benefit PDF to ground it.</div>}
              {(p.sources || []).map((s, i) => (
                <div key={i} className="text-xs flex items-center gap-1.5 text-stone-600 truncate">
                  <span className="rounded bg-stone-100 px-1.5 text-[10px] font-bold">{s.kind.replace("_", " ")}</span>
                  {s.url ? <a href={s.url} target="_blank" rel="noreferrer" className="truncate hover:underline text-[#0B4D3A]">{s.title} <ExternalLink className="w-3 h-3 inline" /></a> : <span className="truncate">{s.title}</span>}
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
